import SuspenseLoading from '@components/SuspenseLoading'
import {pageComponents} from '@data/fragments/website/__generated__/pageComponents'
import {useUserPreferences} from '@data/queries/preferences/userPreferences.main'
import {getSavedValue} from '@hooks/useSessionStorageState'
import useUserId from '@hooks/useUserId'
import useWebsiteInfo from '@hooks/useWebsiteInfo'
import NextOpenDateWrapper from '@page-components/Order/NextOpenDate'
import useLayout from '@page-components/Order/useLayout'
import Page from '@page-components/Page'
import dynamic from 'next/dynamic'

import CreateAddressModal from './CreateAddressModal'
import Products from './Products'
import {SelectedProductIdContextProvider} from './useSelectedProduct'

export interface OrderProps {
  productId?: string
  page?: pageComponents
  websiteId?: string
  componentIdToSsrProps?: {[componentId: string]: any}
}

const LayoutV1 = dynamic(() => import('./layouts/LayoutV1'))
const LayoutV2 = dynamic(() => import('./layouts/LayoutV2'))
const LayoutMobile = dynamic(() => import('./layouts/LayoutMobile'))

const TrackPageView = dynamic(() => import('./TrackPageView'), {ssr: false})
const LoginWithExternalProvider = dynamic(() => import('./LoginWithExternalProvider'), {ssr: false})
const PageMeta = dynamic(() => import('@page-components/Order/PageMeta'), {ssr: false})
const NoMenu = dynamic(() => import('@page-components/Order/NoMenu'))

const getLayout = (layout: string) => {
  switch (layout) {
    case 'v1':
      return LayoutV1
    case 'v2':
      return LayoutV2
    case 'mobile':
      return LayoutMobile
    default:
      return LayoutV2
  }
}

function OrderV2Inner(props: OrderProps) {
  const {productId, page, componentIdToSsrProps} = props
  const layout = useLayout()
  const Layout = getLayout(layout)
  const userPreferences = useUserPreferences()
  const {websiteLoginWithExternalProvidersAvailable} = useWebsiteInfo()
  const userId = useUserId()
  if (!userPreferences.menuId) {
    return <NoMenu />
  }

  const wasOpenedByWebview = getSavedValue('openByWebview')
  const isLoginWithExternalProvider =
    websiteLoginWithExternalProvidersAvailable?.includes(wasOpenedByWebview)

  return (
    <>
      <CreateAddressModal />
      {page ? (
        <Page pageId={page._id} page={page} componentIdToSsrProps={componentIdToSsrProps} />
      ) : null}
      <NextOpenDateWrapper
        deliverAt={userPreferences?.deliverAt}
        storeId={userPreferences?.menuId}
      />
      <Layout>
        <SelectedProductIdContextProvider productId={productId}>
          <Products />
        </SelectedProductIdContextProvider>
      </Layout>

      <TrackPageView />
      {isLoginWithExternalProvider && !userId ? <LoginWithExternalProvider /> : null}

      {productId ? null : <PageMeta />}
    </>
  )
}

export default function OrderV2(props: OrderProps) {
  return <SuspenseLoading>{<OrderV2Inner {...props} />}</SuspenseLoading>
}
